import React, { Fragment } from 'react';
import { Trans } from 'react-i18next';

import { DEFAULT_SAVE_STATUS } from 'src/components/JobChecklistForm/constants';

export const saveStatusReducer = (currentSaveStatus, action) => {
  const { savingBlocks } = currentSaveStatus;

  switch (action.type) {
    case 'ADD_SAVING_BLOCK':
      if (savingBlocks.indexOf(action.block) === -1) {
        return { ...currentSaveStatus, savingBlocks: [...savingBlocks, action.block] };
      }
      return currentSaveStatus;
    case 'SET_SAVED_RESULT': {
      return currentSaveStatus;
    }
    case 'SET_SAVE_STATUS': {
      const newSaveStatus = { ...currentSaveStatus, isFailed: !!action.hasError, retrying: false };

      if (!action.hasError) {
        newSaveStatus.lastSavedBlocks = savingBlocks;
        newSaveStatus.savingBlocks = [];
      }
      return newSaveStatus;
    }
    case 'CLEAR_LAST_SAVED_BLOCK':
      return { ...currentSaveStatus, lastSavedBlocks: [] };
    case 'RETRY_AUTO_SAVE':
      return { ...currentSaveStatus, retrying: true };
    case 'RESET':
      return DEFAULT_SAVE_STATUS;
    default:
      throw new Error(`Unknown saveErrorReducer action type: ${action.type}`);
  }
};

export const getFactoryQuestion = (question, companies) => {
  const company = companies
    .find(cc => question.checklistQuestion.currentVersion.clientSpecificConfigurations
      .find(c => c.companyId === cc.company.id)
    );

  if (!company) {
    return question.checklistQuestion.currentVersion;
  }

  const clientConfiguration = question.checklistQuestion.currentVersion.clientSpecificConfigurations.find(
    c => c.companyId === company.company.id,
  );

  if (clientConfiguration) {
    return {
      ...question.checklistQuestion.currentVersion,
      description: clientConfiguration.description,
      guidance: clientConfiguration.guidance,
      text: clientConfiguration.text,
    };
  }

  return question.checklistQuestion.currentVersion;
};

const [ALL, NOTE_EXISTED, ACTION_NEEDED, NEGATIVE_OUTCOME, CORRECTED_FINDING] = [
  'All',
  'Note',
  'Action needed',
  'Negative outcome',
  'Corrected finding'
];

export const getButtonTextOfFilterBy = (tag, statistic, results, negativeOutcomeResultIds) => {
  switch (tag) {
    case ALL:
      return <Trans>ALL</Trans>;
    case NOTE_EXISTED:
      return (
      <Fragment>
        <Trans>NOTE</Trans>
        {` ${statistic ? statistic.noteExistedQuestionIds.length : 0}`}
      </Fragment>
      );
    case ACTION_NEEDED:
      return (
      <Fragment>
        <Trans>ACTION_NEEDED</Trans>
        {` ${statistic ? statistic.actionRequiredtypeQuestionIds.length : 0}`}
      </Fragment>
      );
    case NEGATIVE_OUTCOME:
      return (
      <Fragment>
        <Trans>NEGATIVE_OUTCOME</Trans>
        {` ${Object.values(results).filter(c => negativeOutcomeResultIds.includes(c.resultId)).length}`}
      </Fragment>
      );
      case CORRECTED_FINDING:
      return (
      <Fragment>
        <Trans>CORRECTED_FINDING</Trans>
        {` ${Object.values(results).filter(c => c.correctedDate).length}`}
      </Fragment>
      );
    default:
      return '';
  }
};

export const getQuestionOutcomes = question => question?.resultFamily?.currentVersion?.resultFamilyOutcomes || null;
export const getChecklistOutcomes = checklist => checklist?.currentVersion?.defaultResultFamily?.currentVersion?.resultFamilyOutcomes || null;

export const getPositiveOutcome = outcomes => outcomes.find(
  v => v.resultCategory.currentVersion.name === 'Positive Outcome',
) || {};
export const getNegativeOutcome = outcomes => outcomes.find(
  v => v.resultCategory.currentVersion.name === 'Negative Outcome',
) || {};

export const getUniqueAlerts = alerts => {
  const returnArr = [];

  alerts.forEach(alert => {
    if (!returnArr.length || !returnArr.some(alert2 => alert2.resultFamilyOutcome.resultId === alert.resultFamilyOutcome.resultId)) {
      returnArr.push(alert);
    }
  });

  return returnArr;
};

export const generateDefaultChecklistResults = (checklist, checklistOutcomes) => {
  const results = [];

  (checklist.currentVersion.checklistSections || []).forEach(section => {
    section.questions.forEach(question => {
      const questionOutcomes = getQuestionOutcomes(question);
      const outcomes = questionOutcomes || checklistOutcomes || [];
      const positiveOutcome = getPositiveOutcome(outcomes);

      results.push({
        attachments: [],
        checklistQuestionId: question.checklistQuestionId,
        currentQuestionId: question.checklistQuestion.currentVersion.id,
        comments: '',
        correctedDate: null,
        correctiveActionTaken: '',
        detailedFinding: '',
        legalRequirements: '',
        nonCompliances: [],
        recommendations: '',
        resultId: positiveOutcome.resultId,
        showToFactoryUser: checklist.currentVersion.showToFactoryUser,
      });
    });
  });

  return results;
};

export const getChecklistMergedResults = (defaultResultsArr, storedResultsArr) => {
  const results = [...storedResultsArr];

  defaultResultsArr.forEach(res1 => {
    if (!results.some(res2 => res2.checklistQuestionId === res1.checklistQuestionId)) {
      results.push(res1);
    }
  });

  return results;
};

export const isPositiveOutcome = (outcome) => {
  return outcome?.resultCategory?.currentVersion?.name === 'Positive Outcome';
};

export const isNegativeOutcome = (outcome) => {
  return outcome?.resultCategory?.currentVersion?.name === 'Negative Outcome';
};

export const LogicType = {
  'Service & Client specific': 'service_and_client_specific',
  'Service specific': 'service_specific',
  'Client specific': 'client_specific'
};
